/**
 * Aldar PDP — vanilla JS controller
 * Handles: gallery (thumbs, zoom, modal, swipe), variant switching (price/image/sku/
 * availability without reload), quantity + add to cart + buy now, sticky cart bar,
 * tabs, share, wishlist and recently-viewed (localStorage, no app).
 *
 * Reuses window.AldarCart (already defined in theme.js) for cart AJAX when present;
 * falls back to Shopify's standard /cart/add.js endpoints otherwise. Does not modify
 * or depend on any change to existing theme files.
 */
(function () {
  'use strict';
  function stripTrailingZeros(str) { return String(str).replace(/(\d)[.,]00(?!\d)/, '$1'); }

  var root = document.querySelector('[data-aldar-pdp]');
  if (!root) return;

  var dataScript = document.querySelector('[data-aldar-product-json]');
  var productData = null;
  try {
    productData = dataScript ? JSON.parse(dataScript.textContent) : null;
  } catch (e) {
    productData = null;
  }
  if (!productData) return;

  var scriptTag = document.currentScript || document.querySelector('script[data-aldar-enable-zoom]');
  var FLAGS = {
    zoom: scriptTag ? scriptTag.getAttribute('data-aldar-enable-zoom') === 'true' : true,
    sticky: scriptTag ? scriptTag.getAttribute('data-aldar-enable-sticky') === 'true' : true,
    recentlyViewed: scriptTag ? scriptTag.getAttribute('data-aldar-enable-recently-viewed') === 'true' : true,
    share: scriptTag ? scriptTag.getAttribute('data-aldar-enable-share') === 'true' : true
  };

  var moneyFormat = productData.moneyFormat || '${{amount}}';

  function formatMoney(cents) {
    if (cents === null || cents === undefined) return '';
    var negative = cents < 0;
    var value = Math.abs(cents) / 100;
    var noDecimals = Math.round(value).toString();
    var withDecimals = value.toFixed(2);
    var decimalParts = withDecimals.split('.');

    function commaSeparate(numStr) {
      return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    function spaceSeparate(numStr) {
      return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    var replacements = {
      amount: withDecimals,
      amount_no_decimals: noDecimals,
      amount_with_comma_separator: commaSeparate(decimalParts[0]) + '.' + decimalParts[1],
      amount_no_decimals_with_comma_separator: commaSeparate(noDecimals),
      amount_with_space_separator: spaceSeparate(decimalParts[0]) + ',' + decimalParts[1],
      amount_no_decimals_with_space_separator: spaceSeparate(noDecimals),
      amount_with_period_and_space_separator: spaceSeparate(decimalParts[0]) + '.' + decimalParts[1]
    };

    var format = moneyFormat || '${{amount}}';
    var result = format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(replacements, key) ? replacements[key] : match;
    });
    return stripTrailingZeros(negative ? '-' + result : result);
  }

  function findVariantById(id) {
    id = String(id);
    for (var i = 0; i < productData.variants.length; i++) {
      if (String(productData.variants[i].id) === id) return productData.variants[i];
    }
    return null;
  }

  /* =====================================================================
     STATE
     ===================================================================== */
  var state = {
    quantity: 1,
    selectedOptions: [],
    currentVariant: null
  };

  (function initState() {
    var select = root.parentElement.querySelector('[data-aldar-variant-select] option[selected]') ||
      document.querySelector('[data-aldar-variant-select] option[selected]');
    var initialVariant = null;
    if (select) {
      initialVariant = findVariantById(select.value);
    }
    if (!initialVariant) {
      var addBtn = document.querySelector('[data-aldar-add-to-cart]');
      if (addBtn) initialVariant = findVariantById(addBtn.getAttribute('data-variant-id'));
    }
    if (!initialVariant) initialVariant = productData.variants[0];
    state.currentVariant = initialVariant;
    state.selectedOptions = initialVariant ? initialVariant.options.slice() : [];
  })();

  /* Gallery + variant-picker wiring below is wrapped in try/catch:
     a single bad assumption in here (e.g. missing DOM node) must never
     abort the rest of the script and take quantity/add-to-cart with it. */
  try {
  /* =====================================================================
     GALLERY
     ===================================================================== */
  var galleryEl = document.querySelector('[data-aldar-gallery]');
  var mainImageEl = document.querySelector('[data-aldar-main-image]');
  var slides = Array.prototype.slice.call(document.querySelectorAll('[data-aldar-media-slide]'));
  var thumbs = Array.prototype.slice.call(document.querySelectorAll('[data-aldar-thumb]'));
  var dots = Array.prototype.slice.call(document.querySelectorAll('[data-aldar-media-dot]'));

  function showMediaById(mediaId) {
    if (!mediaId) return;
    mediaId = String(mediaId);
    var found = false;
    slides.forEach(function (slide, i) {
      var match = slide.getAttribute('data-media-id') === mediaId;
      slide.hidden = !match;
      slide.classList.toggle('active', match);
      if (match) found = true;
    });
    thumbs.forEach(function (thumb) {
      var match = thumb.getAttribute('data-media-id') === mediaId;
      thumb.classList.toggle('active', match);
      thumb.setAttribute('aria-selected', match ? 'true' : 'false');
    });
    if (found) {
      var idx = slides.findIndex(function (s) { return s.getAttribute('data-media-id') === mediaId; });
      dots.forEach(function (dot, i) { dot.classList.toggle('active', i === idx); });
    }
  }

  thumbs.forEach(function (thumb) {
    thumb.addEventListener('click', function () {
      showMediaById(thumb.getAttribute('data-media-id'));
    });
  });

  /* Zoom (cursor-follow on desktop; tap-to-toggle everywhere) */
  if (FLAGS.zoom && mainImageEl) {
    mainImageEl.addEventListener('mousemove', function (e) {
      if (!mainImageEl.classList.contains('zoomed')) return;
      var rect = mainImageEl.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;
      var img = mainImageEl.querySelector('.aldar-media-slide.active .aldar-media-image');
      if (img) img.style.transformOrigin = x + '% ' + y + '%';
    });
    var zoomToggle = document.querySelector('[data-aldar-zoom-toggle]');
    if (zoomToggle) {
      zoomToggle.addEventListener('click', function (e) {
        e.stopPropagation();
        mainImageEl.classList.toggle('zoomed');
      });
    }
    mainImageEl.addEventListener('mouseleave', function () {
      mainImageEl.classList.remove('zoomed');
    });
  }

  /* Full-screen modal */
  var modal = document.querySelector('[data-aldar-gallery-modal]');
  var modalStage = document.querySelector('[data-aldar-modal-stage]');
  var modalIndex = 0;

  function openModalAt(index) {
    if (!modal || !modalStage || slides.length === 0) return;
    modalIndex = ((index % slides.length) + slides.length) % slides.length;
    var slide = slides[modalIndex];
    modalStage.innerHTML = '';
    var clone = slide.cloneNode(true);
    clone.hidden = false;
    clone.removeAttribute('hidden');
    modalStage.appendChild(clone);
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
  }
  function closeModal() {
    if (!modal) return;
    modal.hidden = true;
    document.body.style.overflow = '';
  }
  var openModalBtn = document.querySelector('[data-aldar-open-modal]');
  if (openModalBtn) {
    openModalBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      var activeIdx = slides.findIndex(function (s) { return s.classList.contains('active'); });
      openModalAt(activeIdx === -1 ? 0 : activeIdx);
    });
  }
  var modalClose = document.querySelector('[data-aldar-close-modal]');
  if (modalClose) modalClose.addEventListener('click', closeModal);
  if (modal) {
    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal();
    });
  }
  var modalPrev = document.querySelector('[data-aldar-modal-prev]');
  var modalNext = document.querySelector('[data-aldar-modal-next]');
  if (modalPrev) modalPrev.addEventListener('click', function () { openModalAt(modalIndex - 1); });
  if (modalNext) modalNext.addEventListener('click', function () { openModalAt(modalIndex + 1); });
  document.addEventListener('keydown', function (e) {
    if (!modal || modal.hidden) return;
    if (e.key === 'Escape') closeModal();
    if (e.key === 'ArrowLeft') openModalAt(modalIndex - 1);
    if (e.key === 'ArrowRight') openModalAt(modalIndex + 1);
  });

  /* Mobile swipe on the main image */
  if (mainImageEl) {
    var touchStartX = 0;
    var touchEndX = 0;
    mainImageEl.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    mainImageEl.addEventListener('touchend', function (e) {
      touchEndX = e.changedTouches[0].screenX;
      var delta = touchEndX - touchStartX;
      if (Math.abs(delta) < 40 || slides.length < 2) return;
      var activeIdx = slides.findIndex(function (s) { return s.classList.contains('active'); });
      var nextIdx = delta < 0 ? activeIdx + 1 : activeIdx - 1;
      nextIdx = ((nextIdx % slides.length) + slides.length) % slides.length;
      showMediaById(slides[nextIdx].getAttribute('data-media-id'));
    }, { passive: true });
  }

  /* =====================================================================
     VARIANT PICKER
     ===================================================================== */
  var optionGroups = Array.prototype.slice.call(document.querySelectorAll('[data-aldar-option-values]'));

  function variantForOptions(options) {
    for (var i = 0; i < productData.variants.length; i++) {
      var v = productData.variants[i];
      var match = true;
      for (var j = 0; j < options.length; j++) {
        if (options[j] && v.options[j] !== options[j]) { match = false; break; }
      }
      if (match) return v;
    }
    return null;
  }

  function isValueAvailable(optionIndex, value) {
    for (var i = 0; i < productData.variants.length; i++) {
      var v = productData.variants[i];
      if (v.options[optionIndex] !== value) continue;
      var ok = true;
      for (var j = 0; j < state.selectedOptions.length; j++) {
        if (j === optionIndex) continue;
        if (state.selectedOptions[j] && v.options[j] !== state.selectedOptions[j]) { ok = false; break; }
      }
      if (ok && v.available) return true;
    }
    return false;
  }

  function refreshOptionAvailability() {
    optionGroups.forEach(function (group) {
      var optionIndex = parseInt(group.getAttribute('data-option-index'), 10) - 1;
      var buttons = group.querySelectorAll('[data-aldar-option-value]');
      buttons.forEach(function (btn) {
        var value = btn.getAttribute('data-value');
        var available = isValueAvailable(optionIndex, value);
        btn.classList.toggle('unavailable', !available);
        btn.setAttribute('aria-disabled', available ? 'false' : 'true');
      });
    });
  }

  function updateSelectedLabels() {
    document.querySelectorAll('[data-aldar-option-group]').forEach(function (group) {
      var idx = parseInt(group.getAttribute('data-option-position'), 10) - 1;
      var label = group.querySelector('[data-aldar-option-selected]');
      if (label) label.textContent = state.selectedOptions[idx] || '';
    });
  }

  function applyVariant(variant, opts) {
    opts = opts || {};
    state.currentVariant = variant;

    /* price */
    document.querySelectorAll('[data-aldar-price]').forEach(function (priceEl) {
      var now = priceEl.querySelector('[data-aldar-price-current]');
      var was = priceEl.querySelector('[data-aldar-price-compare]');
      var save = priceEl.querySelector('[data-aldar-price-save]');
      if (now) now.textContent = formatMoney(variant.price);
      var onSale = variant.compareAtPrice && variant.compareAtPrice > variant.price;
      if (was) {
        if (onSale) { was.textContent = formatMoney(variant.compareAtPrice); was.style.display = ''; }
        else { was.style.display = 'none'; }
      }
      if (save) {
        if (onSale) {
          var pct = Math.round(((variant.compareAtPrice - variant.price) / variant.compareAtPrice) * 100);
          save.textContent = save.textContent.replace(/\d+%/, pct + '%');
          save.style.display = '';
        } else {
          save.style.display = 'none';
        }
      }
    });

    /* availability */
    document.querySelectorAll('[data-aldar-availability]').forEach(function (el) {
      var stock = el.querySelector('.aldar-stock');
      if (stock) {
        stock.classList.toggle('aldar-stock--in', !!variant.available);
        stock.classList.toggle('aldar-stock--out', !variant.available);
      }
    });

    /* sku */
    document.querySelectorAll('[data-aldar-spec-sku]').forEach(function (el) {
      el.textContent = variant.sku || '';
    });

    /* add to cart + buy now buttons */
    document.querySelectorAll('[data-aldar-add-to-cart]').forEach(function (btn) {
      btn.setAttribute('data-variant-id', variant.id);
      btn.disabled = !variant.available;
      var label = btn.querySelector('[data-aldar-add-to-cart-label]');
      if (label) {
        if (variant.available) {
          var isStickyBar = !!btn.closest('[data-aldar-sticky-bar]');
          label.textContent = isStickyBar ? 'Add to Cart' : 'Add to Cart — ' + formatMoney(variant.price);
        } else {
          label.textContent = 'Sold Out';
        }
      }
    });
    document.querySelectorAll('[data-aldar-buy-now]').forEach(function (btn) {
      btn.setAttribute('data-variant-id', variant.id);
      btn.disabled = !variant.available;
    });

    /* installments */
    document.querySelectorAll('[data-aldar-installments]').forEach(function (el) {
      var installments = parseInt(el.getAttribute('data-installments'), 10) || 4;
      var amountEl = el.querySelector('[data-aldar-installment-amount]');
      if (amountEl) amountEl.textContent = formatMoney(Math.round(variant.price / installments));
    });

    /* sticky bar */
    document.querySelectorAll('[data-aldar-sticky-price]').forEach(function (el) {
      var variantLabel = el.querySelector('[data-aldar-sticky-variant]');
      var text = formatMoney(variant.price);
      if (variantLabel) {
        variantLabel.textContent = variant.title;
        var leadingTextNode = null;
        for (var n = 0; n < el.childNodes.length; n++) {
          if (el.childNodes[n].nodeType === 3) { leadingTextNode = el.childNodes[n]; break; }
        }
        if (leadingTextNode) {
          leadingTextNode.textContent = text + ' ';
        } else {
          el.insertBefore(document.createTextNode(text + ' '), el.firstChild);
        }
      } else {
        el.textContent = text;
      }
    });

    /* gallery: switch to this variant's image if it has one */
    if (variant.featuredMediaId && !opts.skipGallery) {
      showMediaById(variant.featuredMediaId);
    }

    /* url + hidden select, for shareable links & form compatibility */
    if (!opts.skipUrl && window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
    var hiddenSelect = document.querySelector('[data-aldar-variant-select]');
    if (hiddenSelect) hiddenSelect.value = variant.id;

    updateSelectedLabels();
    refreshOptionAvailability();
  }

  optionGroups.forEach(function (group) {
    group.addEventListener('click', function (e) {
      var target = e.target.closest('[data-aldar-option-value]');
      if (!target || target.getAttribute('aria-disabled') === 'true') return;
      var optionIndex = parseInt(target.getAttribute('data-option-index'), 10) - 1;
      var value = target.getAttribute('data-value');

      state.selectedOptions[optionIndex] = value;

      group.querySelectorAll('[data-aldar-option-value]').forEach(function (b) {
        b.classList.remove('active');
        b.setAttribute('aria-pressed', 'false');
      });
      target.classList.add('active');
      target.setAttribute('aria-pressed', 'true');

      var matched = variantForOptions(state.selectedOptions);
      if (matched) applyVariant(matched);
      else updateSelectedLabels();
    });
  });

  /* keep gallery/labels correct on first paint even if a ?variant= URL param
     points at a different variant than server-selected (deep link support) */
  (function syncFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var vid = params.get('variant');
    if (vid && String(vid) !== String(state.currentVariant.id)) {
      var v = findVariantById(vid);
      if (v) {
        state.selectedOptions = v.options.slice();
        applyVariant(v, { skipUrl: true, skipGallery: true });
      }
    }
  })();
  refreshOptionAvailability();

  } catch (err) {
    if (window.console) console.error('Aldar PDP: gallery/variant setup failed, continuing anyway', err);
  }

  /* =====================================================================
     QUANTITY
     ===================================================================== */
  function setQuantity(qty) {
    qty = Math.max(1, qty);
    state.quantity = qty;
    document.querySelectorAll('[data-aldar-qty-value]').forEach(function (el) {
      el.textContent = qty;
    });
  }
  document.querySelectorAll('[data-aldar-qty-minus]').forEach(function (btn) {
    btn.addEventListener('click', function () { setQuantity(state.quantity - 1); });
  });
  document.querySelectorAll('[data-aldar-qty-plus]').forEach(function (btn) {
    btn.addEventListener('click', function () { setQuantity(state.quantity + 1); });
  });

  /* =====================================================================
     TOAST
     ===================================================================== */
  var toastEl = null;
  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'aldar-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.classList.add('visible');
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(function () { toastEl.classList.remove('visible'); }, 2200);
  }

  /* =====================================================================
     ADD TO CART / BUY NOW
     ===================================================================== */
  function cartAdd(variantId, qty) {
    if (window.AldarCart && typeof window.AldarCart.add === 'function') {
      return window.AldarCart.add(variantId, qty);
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    });
  }

  function refreshCartBadge() {
    if (window.AldarCart && typeof window.AldarCart.refreshBadge === 'function') {
      window.AldarCart.refreshBadge();
    }
  }

  function openCartDrawer() {
    if (window.AldarCartDrawer && typeof window.AldarCartDrawer.refresh === 'function') {
      window.AldarCartDrawer.refresh().then(window.AldarCartDrawer.open);
      return true;
    }
    return false;
  }

  document.addEventListener('click', function (e) {
    var addBtn = e.target.closest('[data-aldar-add-to-cart]');
    if (addBtn && !addBtn.disabled) {
      var variantId = addBtn.getAttribute('data-variant-id');
      addBtn.classList.add('is-loading');
      cartAdd(variantId, state.quantity)
        .then(function () {
          refreshCartBadge();
          if (!openCartDrawer()) toast('Added to cart');
        })
        .catch(function () {
          toast('Could not add to cart — please try again');
        })
        .finally(function () {
          addBtn.classList.remove('is-loading');
        });
      return;
    }

    var buyBtn = e.target.closest('[data-aldar-buy-now]');
    if (buyBtn && !buyBtn.disabled) {
      var bVariantId = buyBtn.getAttribute('data-variant-id');
      buyBtn.classList.add('is-loading');
      cartAdd(bVariantId, state.quantity)
        .then(function () {
          window.location.href = '/checkout';
        })
        .catch(function () {
          buyBtn.classList.remove('is-loading');
          toast('Could not start checkout — please try again');
        });
      return;
    }
  });

  /* =====================================================================
     WISHLIST (localStorage, no app)
     ===================================================================== */
  var WISHLIST_KEY = 'aldar_wishlist';
  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []; } catch (e) { return []; }
  }
  function setWishlist(list) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }
  }
  function syncWishlistButton(btn) {
    var handle = btn.getAttribute('data-product-handle');
    var active = getWishlist().indexOf(handle) !== -1;
    btn.classList.toggle('active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  }
  document.querySelectorAll('[data-aldar-wishlist]').forEach(function (btn) {
    syncWishlistButton(btn);
    btn.addEventListener('click', function () {
      var handle = btn.getAttribute('data-product-handle');
      var list = getWishlist();
      var idx = list.indexOf(handle);
      if (idx === -1) { list.push(handle); toast('Added to wishlist'); }
      else { list.splice(idx, 1); toast('Removed from wishlist'); }
      setWishlist(list);
      syncWishlistButton(btn);
      btn.classList.add('is-pulsing');
      setTimeout(function () { btn.classList.remove('is-pulsing'); }, 350);
    });
  });

  /* =====================================================================
     SHARE
     ===================================================================== */
  if (FLAGS.share) {
    document.querySelectorAll('[data-aldar-share-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        var title = trigger.getAttribute('data-share-title');
        var url = trigger.getAttribute('data-share-url');
        var text = trigger.getAttribute('data-share-text');

        if (navigator.share) {
          navigator.share({ title: title, text: text, url: url }).catch(function () { /* user cancelled */ });
          return;
        }
        var menu = trigger.parentElement.querySelector('[data-aldar-share-menu]');
        if (menu) {
          var isHidden = menu.hasAttribute('hidden');
          document.querySelectorAll('[data-aldar-share-menu]').forEach(function (m) { m.setAttribute('hidden', ''); });
          if (isHidden) menu.removeAttribute('hidden');
          else menu.setAttribute('hidden', '');
          trigger.setAttribute('aria-expanded', isHidden ? 'true' : 'false');
        }
      });
    });
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-aldar-share]')) {
        document.querySelectorAll('[data-aldar-share-menu]').forEach(function (m) { m.setAttribute('hidden', ''); });
      }
      var copyBtn = e.target.closest('[data-aldar-share-copy]');
      if (copyBtn) {
        var url = copyBtn.closest('[data-aldar-share]').querySelector('[data-aldar-share-trigger]').getAttribute('data-share-url');
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(function () { toast('Link copied'); });
        }
      }
    });
  }

  /* =====================================================================
     TABS
     ===================================================================== */
  var tabTriggers = Array.prototype.slice.call(document.querySelectorAll('[data-aldar-tab-trigger]'));
  tabTriggers.forEach(function (trigger) {
    trigger.addEventListener('click', function () {
      var id = trigger.getAttribute('data-aldar-tab-trigger');
      tabTriggers.forEach(function (t) {
        var active = t === trigger;
        t.classList.toggle('active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      document.querySelectorAll('[data-aldar-tab-panel]').forEach(function (panel) {
        panel.classList.toggle('active', panel.getAttribute('data-aldar-tab-panel') === id);
      });
    });
  });

  /* =====================================================================
     STICKY ADD-TO-CART BAR
     ===================================================================== */
  if (FLAGS.sticky) {
    var stickyBar = document.querySelector('[data-aldar-sticky-bar]');
    var anchor = document.querySelector('[data-aldar-buy-buttons] [data-aldar-add-to-cart]');
    if (stickyBar && anchor && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          stickyBar.classList.toggle('visible', show);
          stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
        });
      }, { threshold: 0 });
      observer.observe(anchor);
    }
  }

  /* =====================================================================
     RECENTLY VIEWED (localStorage, no app)
     ===================================================================== */
  if (FLAGS.recentlyViewed) {
    var RV_KEY = 'aldar_recently_viewed';
    var MAX_ITEMS = 8;

    function getRecentlyViewed() {
      try { return JSON.parse(localStorage.getItem(RV_KEY)) || []; } catch (e) { return []; }
    }
    function setRecentlyViewed(list) {
      try { localStorage.setItem(RV_KEY, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }
    }

    function renderRecentlyViewed() {
      var list = getRecentlyViewed().filter(function (item) { return item.handle !== productData.handle; });
      var container = document.querySelector('[data-aldar-recently-viewed-grid]');
      var section = document.querySelector('[data-aldar-recently-viewed]');
      if (!container || !section) return;
      if (list.length === 0) { section.hidden = true; return; }

      container.innerHTML = '';
      list.slice(0, 4).forEach(function (item) {
        var a = document.createElement('a');
        a.className = 'product reveal in';
        a.href = item.url;
        var priceHtml = '<span class="now">' + escapeHtml(item.priceFormatted || '') + '</span>';
        if (item.compareAtPriceFormatted) {
          priceHtml = '<span class="was">' + escapeHtml(item.compareAtPriceFormatted) + '</span>' + priceHtml;
        }
        a.innerHTML =
          '<div class="product-image">' +
            '<img class="product-image-main" src="' + escapeHtml(item.image || '') + '" alt="' + escapeHtml(item.title) + '" loading="lazy" width="300" height="300">' +
          '</div>' +
          '<div class="product-info">' +
            (item.vendor ? '<div class="product-brand">' + escapeHtml(item.vendor) + '</div>' : '') +
            '<h3 class="product-name">' + escapeHtml(item.title) + '</h3>' +
            '<div class="product-price">' + priceHtml + '</div>' +
          '</div>';
        container.appendChild(a);
      });
      section.hidden = false;
    }

    function escapeHtml(str) {
      var div = document.createElement('div');
      div.textContent = str == null ? '' : String(str);
      return div.innerHTML;
    }

    function recordCurrentProduct() {
      var list = getRecentlyViewed().filter(function (item) { return item.handle !== productData.handle; });
      var variant = state.currentVariant || productData.variants[0];
      list.unshift({
        handle: productData.handle,
        title: productData.title,
        vendor: productData.vendor,
        url: productData.url,
        image: productData.featuredImage,
        priceFormatted: formatMoney(variant.price),
        compareAtPriceFormatted: variant.compareAtPrice && variant.compareAtPrice > variant.price ? formatMoney(variant.compareAtPrice) : null
      });
      if (list.length > MAX_ITEMS) list = list.slice(0, MAX_ITEMS);
      setRecentlyViewed(list);
    }

    renderRecentlyViewed();
    recordCurrentProduct();
  }
})();

/* =====================================================================
   RECENTLY VIEWED - standalone failsafe module
   Runs independently of the main controller, so a script error anywhere
   else on the page can never stop Recently Viewed from working.
   Idempotent: safe even when the main module already ran.
   ===================================================================== */
(function () {
  'use strict';
  try {
    var enableTag = document.querySelector('script[data-aldar-enable-recently-viewed], script[data-cl-enable-recently-viewed]');
    if (enableTag) {
      var flag = enableTag.getAttribute('data-aldar-enable-recently-viewed') || enableTag.getAttribute('data-cl-enable-recently-viewed');
      if (flag === 'false') return;
    }

    var dataScript = document.querySelector('[data-aldar-product-json], [data-cl-product-json]');
    if (!dataScript) return;
    var pd = null;
    try { pd = JSON.parse(dataScript.textContent); } catch (e) { return; }
    if (!pd || !pd.handle) return;

    var section = document.querySelector('[data-aldar-recently-viewed], [data-cl-recently-viewed]');
    var grid = document.querySelector('[data-aldar-recently-viewed-grid], [data-cl-recently-viewed-grid]');

    var KEY = 'aldar_recently_viewed';
    var MAX = 8;

    function read() {
      try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; }
    }
    function write(list) {
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    }
    function money(cents) {
      if (cents === null || cents === undefined) return '';
      var v = (cents / 100).toFixed(2);
      var fmt = pd.moneyFormat || '{{amount}}';
      var noDec = (cents / 100).toFixed(0);
      var comma = v.replace('.', ',');
      return fmt
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, comma)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
        .replace(/\{\{\s*amount\s*\}\}/g, v)
        .replace(/<[^>]*>/g, '').replace(/(\d)[.,]00(?!\d)/, '$1');
    }
    function esc(str) {
      var d = document.createElement('div');
      d.textContent = str == null ? '' : String(str);
      return d.innerHTML;
    }

    /* 1) record this product (idempotent) */
    var v0 = (pd.variants && pd.variants[0]) || {};
    var list = read().filter(function (i) { return i && i.handle !== pd.handle; });
    list.unshift({
      handle: pd.handle,
      title: pd.title,
      vendor: pd.vendor,
      url: pd.url,
      image: pd.featuredImage,
      priceFormatted: money(v0.price),
      compareAtPriceFormatted: v0.compareAtPrice && v0.compareAtPrice > v0.price ? money(v0.compareAtPrice) : null
    });
    if (list.length > MAX) list = list.slice(0, MAX);
    write(list);

    /* 2) render previous products (excluding the current one) */
    if (!section || !grid) return;
    var show = read().filter(function (i) { return i && i.handle !== pd.handle; });
    if (show.length === 0) { section.hidden = true; return; }

    grid.innerHTML = '';
    show.slice(0, 4).forEach(function (item) {
      var a = document.createElement('a');
      a.className = 'product reveal in';
      a.href = item.url;
      var priceHtml = '<span class="now">' + esc(item.priceFormatted || '') + '</span>';
      if (item.compareAtPriceFormatted) {
        priceHtml = '<span class="was">' + esc(item.compareAtPriceFormatted) + '</span>' + priceHtml;
      }
      a.innerHTML =
        '<div class="product-image">' +
          '<img class="product-image-main" src="' + esc(item.image || '') + '" alt="' + esc(item.title) + '" loading="lazy" width="300" height="300">' +
        '</div>' +
        '<div class="product-info">' +
          (item.vendor ? '<div class="product-brand">' + esc(item.vendor) + '</div>' : '') +
          '<h3 class="product-name">' + esc(item.title) + '</h3>' +
          '<div class="product-price">' + priceHtml + '</div>' +
        '</div>';
      grid.appendChild(a);
    });
    section.hidden = false;
  } catch (e) { /* never break the page */ }
})();